import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useColorModeValue,
  Text,
  Flex,
  Textarea,
  Checkbox,
  Input,
  Box,
  VStack,
  HStack,
  Divider,
  Spinner,
  Badge,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { getPostMessage } from "utils/getPostMessage";
import { PROPERTIES_LISTING_API } from "constants/endpoints";
import useApi from "hooks/useApi";
import { HttpStatusCode } from "axios";

const PropertiesModal = ({
  isOpen,
  onClose,
  title,
  desc,
  withListingScore,
  withShareToSocialMedia,
  withEmailCheckBox,
  propertyData,
  onSubmit,
  showPostInput,
  initialData = {},
}) => {
  const [formData, setFormData] = useState({});
  const { listingScore, shareToSocialMedia, message, sendEmail } =
    formData || {};
  const [showPostMessage, setShowPostMessage] = useState(false);
  const { apiCall, loading } = useApi();
  const [ownerProperties, setOwnerProperties] = useState({
    count: 0,
    data: [],
  });

  // Color mode values - All hooks must be at top level
  const bgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const buttonColorScheme = useColorModeValue("blue", "teal");
  const propertyBgColor = useColorModeValue("gray.50", "gray.800");
  const propertyHoverBgColor = useColorModeValue("gray.100", "gray.750");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const mutedTextColor = useColorModeValue("gray.600", "gray.400");
  const statusBgExpired = useColorModeValue("red.50", "red.900");
  const statusTextExpired = useColorModeValue("red.700", "red.200");
  const scrollbarBgTrack = useColorModeValue("gray.100", "gray.800");
  const scrollbarBgThumb = useColorModeValue("gray.400", "gray.600");
  const scrollbarBgThumbHover = useColorModeValue("gray.500", "gray.500");
  const scrollbarBg200 = useColorModeValue("gray.200", "gray.700");

  const isButtonDisabled = useMemo(() => {
    if (withListingScore && (listingScore > 100 || listingScore < 0))
      return true;
    return false;
  }, [listingScore, withListingScore]);

  const handleSetModalData = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const closeModal = () => {
    setFormData(initialData);
    onClose();
  };

  useEffect(() => {
    getOwnerProperties();
    const messagePreview = getPostMessage(propertyData);
    if (formData?.message !== messagePreview) {
      handleSetModalData("message", messagePreview);
    }
  }, [propertyData]);

  useEffect(() => {
    if (JSON.stringify(initialData) !== JSON.stringify(formData)) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    setShowPostMessage(showPostInput);
  }, [showPostInput]);

  async function getOwnerProperties() {
    if (!propertyData?.owner?.phone) return;
    const body = {
      search_text: propertyData?.owner?.phone || "",
      limit: 500
    };
    try {
      const res = await apiCall.get(PROPERTIES_LISTING_API, body);
      if (res?.statusCode === HttpStatusCode.Ok) {
        const { count, data = [] } = res?.data || {};
        const result = data?.filter(property => property._id !== propertyData?._id);
        const finalCount = count - 1 >= 0 ? count - 1 : 0;
        setOwnerProperties({ count: finalCount, data: result });
      }
    } catch (error) {
      console.log("Error while fetching properties: ", error.message);
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadgeColor = (status) => {
    const statusColorMap = {
      pending_verification: { bg: "yellow.50", text: "yellow.700" },
      active: { bg: "green.50", text: "green.700" },
      expired: { bg: statusBgExpired, text: statusTextExpired },
      deleted: { bg: "gray.100", text: "gray.700" },
      inactive: { bg: "orange.50", text: "orange.700" },
      rejected: { bg: "red.100", text: "red.700" },
    };
    return statusColorMap[status] || { bg: "blue.50", text: "blue.700" };
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      isCentered
      motionPreset="scale"
      size="lg"
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent
        bg={bgColor}
        boxShadow="2xl"
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        mx={4}
        p={6}
        width="100%"
        maxWidth="750px"
        maxHeight="90vh"
        display="flex"
        flexDirection="column"
      >
        <ModalHeader fontSize="xl" fontWeight="700" color="black" pb={4}>
          {title}
        </ModalHeader>
        <ModalCloseButton top={6} right={6} />

        {/* Scrollable Body */}
        <ModalBody
          overflowY="auto"
          overflowX="hidden"
          px={6}
          pb={6}
          flex="1"
          css={{
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: scrollbarBgTrack,
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: scrollbarBgThumb,
              borderRadius: "10px",
              "&:hover": {
                background: scrollbarBgThumbHover,
              },
            },
          }}
        >
          <VStack spacing={6} align="stretch">
            {/* Description */}
            <Box>
              <Text
                fontSize="md"
                color={textColor}
                lineHeight="1.6"
                dangerouslySetInnerHTML={{ __html: desc }}
              />
            </Box>

            {/* Listing Score Section */}
            {withListingScore && (
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" fontWeight="600" color="black">
                  Update Listing Score
                </Text>
                <Input
                  type="number"
                  value={listingScore || ""}
                  onChange={(e) =>
                    handleSetModalData("listingScore", e.target.value)
                  }
                  placeholder="Enter score (0-100)"
                  borderColor={borderColor}
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                  }}
                  size="md"
                />
                {listingScore && (listingScore > 100 || listingScore < 0) && (
                  <Text fontSize="xs" color="red.500" fontWeight="500">
                    Score must be between 0 and 100
                  </Text>
                )}
              </VStack>
            )}

            {/* Share to Social Media Section */}
            {withShareToSocialMedia && (
              <VStack spacing={3} align="stretch">
                <Checkbox
                  isChecked={shareToSocialMedia}
                  defaultChecked
                  onChange={(e) => {
                    const value = e.target.checked;
                    if (value) {
                      setShowPostMessage(true);
                      handleSetModalData("shareToSocialMedia", value);
                    } else {
                      setShowPostMessage(false);
                      handleSetModalData("shareToSocialMedia", value);
                    }
                  }}
                  fontSize="md"
                  fontWeight="500"
                  colorScheme="blue"
                >
                  Share To Social Media as well?
                </Checkbox>
              </VStack>
            )}

            {/* Post Preview Section */}
            {showPostMessage && (
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" fontWeight="600" color="black">
                  Post Preview
                </Text>
                <Textarea
                  value={message || ""}
                  onChange={(e) =>
                    handleSetModalData("message", e.target.value)
                  }
                  placeholder="Please add Message"
                  size="sm"
                  resize="vertical"
                  height={180}
                  borderColor={borderColor}
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                  }}
                  fontSize="sm"
                />
                {withEmailCheckBox && (
                  <Checkbox
                    isChecked={sendEmail}
                    onChange={(e) =>
                      handleSetModalData("sendEmail", e.target.checked)
                    }
                    fontSize="md"
                    fontWeight="500"
                    colorScheme="blue"
                  >
                    Send Email
                  </Checkbox>
                )}
              </VStack>
            )}

            {/* Other Properties Section */}
            {ownerProperties?.count > 0 && (
              <VStack spacing={4} align="stretch">
                <Divider my={2} borderColor={borderColor} />
                <Box>
                  <HStack spacing={2} mb={4}>
                    <Text fontSize="sm" fontWeight="600" color="black">
                      Owner's Other Properties
                    </Text>
                    <Badge
                      colorScheme="blue"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      fontWeight="600"
                    >
                      {ownerProperties.count}
                    </Badge>
                  </HStack>

                  {loading ? (
                    <Flex
                      justify="center"
                      align="center"
                      py={12}
                      flexDirection="column"
                      gap={3}
                    >
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor={scrollbarBg200}
                        color="blue.400"
                        size="lg"
                      />
                      <Text fontSize="sm" color={mutedTextColor}>
                        Loading properties...
                      </Text>
                    </Flex>
                  ) : (
                    <VStack spacing={3} align="stretch">
                      {ownerProperties.data.map((property, index) => {
                        const statusColor = getStatusBadgeColor(
                          property?.property_status
                        );
                        return (
                          <ChakraLink
                            key={property._id || index}
                            href={`${process.env.REACT_APP_HEXAHOME_BASE_URL}/property-details/${property.slug_url}`}
                            _hover={{ textDecoration: "none" }}
                            isExternal={false}
                            target="_blank"
                          >
                            <Box
                              bg={propertyBgColor}
                              p={4}
                              borderRadius="md"
                              borderWidth="1px"
                              borderColor={borderColor}
                              transition="all 0.2s ease-in-out"
                              _hover={{
                                bg: propertyHoverBgColor,
                                borderColor: "blue.300",
                                shadow: "md",
                                transform: "translateY(-2px)",
                              }}
                              cursor="pointer"
                            >
                              <VStack spacing={3} align="stretch">
                                {/* Property Slug */}
                                <HStack
                                  justify="space-between"
                                  align="flex-start"
                                >
                                  <Text
                                    fontSize="sm"
                                    fontWeight="600"
                                    color="blue.600"
                                    lineHeight="1.4"
                                    flex={1}
                                    noOfLines={2}
                                  >
                                    {property.slug_url}
                                  </Text>
                                  <ChevronRightIcon
                                    color="blue.400"
                                    fontSize="lg"
                                    mt={1}
                                    flexShrink={0}
                                  />
                                </HStack>

                                {/* Property Details Row */}
                                <HStack
                                  spacing={3}
                                  justify="space-between"
                                  fontSize="xs"
                                >
                                  {/* Status */}
                                  <Badge
                                    bg={statusColor.bg}
                                    color={statusColor.text}
                                    fontWeight="600"
                                    px={2.5}
                                    py={1}
                                    borderRadius="md"
                                    textTransform="capitalize"
                                  >
                                    {property.property_status || "N/A"}
                                  </Badge>

                                  {/* Created Date */}
                                  <Text color={mutedTextColor} fontWeight="500">
                                    {formatDate(property.created_at)}
                                  </Text>
                                </HStack>
                              </VStack>
                            </Box>
                          </ChakraLink>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              </VStack>
            )}

            {/* No Properties Message */}
            {!loading && ownerProperties?.count === 0 && (
              <Box
                py={8}
                px={4}
                bg={propertyBgColor}
                borderRadius="md"
                textAlign="center"
                borderWidth="1px"
                borderColor={borderColor}
                borderStyle="dashed"
              >
                <Text fontSize="sm" color={mutedTextColor}>
                  No other properties from this owner
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        {/* Footer */}
        <ModalFooter
          justifyContent="space-between"
          pt={6}
          borderTop="1px"
          borderColor={borderColor}
        >
          <Button
            colorScheme={buttonColorScheme}
            isDisabled={isButtonDisabled}
            onClick={() => onSubmit(formData)}
            mx={1}
            background="linear-gradient(180deg, #22ACEE 0%, #0177B4 100%)"
            _hover={{
              background: "linear-gradient(180deg, #1A94D4 0%, #016299 100%)",
            }}
            _active={{
              background: "linear-gradient(180deg, #158AC2 0%, #01517E 100%)",
            }}
            fontWeight="600"
            size="md"
            _disabled={{
              opacity: 0.5,
              cursor: "not-allowed",
            }}
          >
            Yes, update
          </Button>
          <Button
            variant="outline"
            onClick={closeModal}
            mx={1}
            fontWeight="600"
            size="md"
            borderColor={borderColor}
            _hover={{
              bg: propertyBgColor,
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PropertiesModal;
